/**
 * Utility functions for fetching and validating book covers from multiple sources
 */

/**
 * Check if a URL returns a valid image
 */
export const checkImageExists = async (url) => {
  if (!url) return false;

  try {
    const response = await fetch(url, {
      method: "HEAD",
      signal: AbortSignal.timeout(3000), // 3 second timeout
    });

    if (!response.ok) return false;

    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    // Check if it's an image and not empty
    return (
      contentType?.startsWith("image/") &&
      contentLength &&
      parseInt(contentLength) > 0
    );
  } catch (error) {
    console.error("Error checking image:", error);
    return false;
  }
};

/**
 * Detect if a URL is a placeholder/generic cover
 */
export const isPlaceholderImage = (url) => {
  if (!url) return true;

  const placeholderPatterns = [
    "books/content?id=", // Google Books placeholder
    "books/publisher/content", // Google Books publisher placeholder
    "googleusercontent.com/books/anyimage", // Generic Google image
    "default-book-cover.png", // Our default
    "/images/no-cover", // Common no-cover pattern
    "placeholder", // Generic placeholder
  ];

  return placeholderPatterns.some((pattern) =>
    url.toLowerCase().includes(pattern)
  );
};

/**
 * Get Open Library cover URL by ISBN
 */
export const getOpenLibraryCover = async (isbn, size = "L") => {
  if (!isbn) return null;

  // Clean ISBN (remove hyphens)
  const cleanIsbn = isbn.replace(/-/g, "");

  const url = `https://covers.openlibrary.org/b/isbn/${cleanIsbn}-${size}.jpg`;

  // Check if the cover exists
  const exists = await checkImageExists(url);
  return exists ? url : null;
};

/**
 * Get the best available cover from multiple sources
 */
export const getBestCover = async (googleImageLinks, isbn) => {
  const { getHighestQualityImage } = await import("./imageUtils");

  // 1. Try Google Books first
  const googleCover = getHighestQualityImage(googleImageLinks);

  // 2. If Google has a good cover (not placeholder), use it
  if (googleCover && !isPlaceholderImage(googleCover)) {
    return {
      url: googleCover,
      source: "google",
    };
  }

  // 3. Try Open Library as fallback
  if (isbn) {
    const openLibraryCover = await getOpenLibraryCover(isbn);
    if (openLibraryCover) {
      return {
        url: openLibraryCover,
        source: "openlibrary",
      };
    }
  }

  // 4. Use Google cover even if it's a placeholder (better than nothing)
  if (googleCover) {
    return {
      url: googleCover,
      source: "google",
    };
  }

  // 5. Final fallback
  return {
    url: "/images/default-book-cover.png",
    source: "default",
  };
};
